// Parser/validator/formatter del W3C Trace Context (`traceparent`/`tracestate`)
// e del W3C Baggage, più la decisione di propagazione via allowlist. Modulo puro
// e isomorfo (nessun accesso a window/process): il glue con scope e opzioni vive
// nel `Client`. Riferimenti: W3C Trace Context (traceparent/tracestate) e W3C
// Baggage. Il `trace-id` W3C (32 hex) coincide col `trace_id` interno CloseYourIt
// (UUID v4 senza trattini), così adottare un trace distribuito è un mapping 1:1.

import type { TraceCarrier, TracePropagationTarget } from './types'

/// Trace context W3C estratto da un `traceparent` valido.
export interface TraceParent {
  /// `trace-id`: 32 esadecimali lowercase, non tutto-zero.
  traceId: string
  /// `parent-id` (span-id del chiamante): 16 esadecimali lowercase, non tutto-zero.
  parentId: string
  /// Bit 0 di `trace-flags` (`sampled`): il trace a monte è campionato.
  sampled: boolean
}

const HEX2 = /^[0-9a-f]{2}$/
const HEX16 = /^[0-9a-f]{16}$/
const HEX32 = /^[0-9a-f]{32}$/
const ZERO_TRACE = '0'.repeat(32)
const ZERO_PARENT = '0'.repeat(16)

/// Interpreta un header `traceparent` W3C. Ritorna il trace context, oppure
/// `null` se assente o malformato (lowercase-only, campi di lunghezza fissa,
/// trace-id/parent-id non tutto-zero, version `ff` riservata rifiutata). Una
/// version futura (> `00`) è accettata leggendo i primi quattro campi e ignorando
/// gli eventuali campi aggiuntivi, come raccomanda lo standard.
export function parseTraceparent(value: string | null | undefined): TraceParent | null {
  if (!value) return null
  const parts = value.trim().split('-')
  if (parts.length < 4) return null

  const version = parts[0]!
  const traceId = parts[1]!
  const parentId = parts[2]!
  const flags = parts[3]!

  if (!HEX2.test(version) || version === 'ff') return null
  // La version corrente (`00`) è definita esattamente su quattro campi: nessun
  // campo extra ammesso. Le version future possono aggiungerne (ignorati).
  if (version === '00' && parts.length !== 4) return null
  if (!HEX32.test(traceId) || traceId === ZERO_TRACE) return null
  if (!HEX16.test(parentId) || parentId === ZERO_PARENT) return null
  if (!HEX2.test(flags)) return null

  return { traceId, parentId, sampled: (parseInt(flags, 16) & 0x01) === 0x01 }
}

/// Compone un `traceparent` version `00` da trace-id, span-id e flag di sampling.
/// I due id sono attesi già validi (32 e 16 esadecimali).
export function formatTraceparent(
  traceId: string,
  spanId: string,
  sampled: boolean,
): string {
  return `00-${traceId}-${spanId}-${sampled ? '01' : '00'}`
}

// key = lcalpha *( lcalpha / DIGIT / "_" / "-" / "*" / "/" ) con tenant "@vendor"
// opzionale (semplificazione conforme del grammar W3C tracestate).
const TRACESTATE_KEY = /^[a-z0-9][a-z0-9_\-*/]*(@[a-z0-9][a-z0-9_\-*/]*)?$/

function isValidTracestateMember(member: string): boolean {
  const eq = member.indexOf('=')
  if (eq <= 0) return false
  const key = member.slice(0, eq)
  const val = member.slice(eq + 1)
  if (!TRACESTATE_KEY.test(key)) return false
  if (val.length === 0 || val.length > 256) return false
  // value: printable ASCII (la virgola è già consumata come separatore di lista
  // da `parseTracestate`, che splitta prima di validare i membri).
  return /^[\x20-\x7e]*$/.test(val)
}

/// Valida e normalizza un header `tracestate` come stringa opaca da ri-propagare:
/// scarta i membri malformati, tronca a 32 membri (limite W3C). Ritorna
/// `undefined` se assente o interamente invalido.
export function parseTracestate(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const valid = value
    .split(',')
    .map((m) => m.trim())
    .filter((m) => m.length > 0 && isValidTracestateMember(m))
    .slice(0, 32)
  return valid.length > 0 ? valid.join(',') : undefined
}

/// Massimo dimensionamento raccomandato per l'header `baggage` (W3C Baggage).
const MAX_BAGGAGE_BYTES = 8192

/// Valida un header `baggage` come stringa opaca da ri-propagare SOLO verso
/// destinazioni fidate: può contenere PII, quindi non va mai allegato agli eventi
/// né inviato a host non consentiti. Ritorna `undefined` se vuoto, non-printable
/// o oltre il limite.
export function normalizeBaggage(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  if (trimmed.length === 0 || trimmed.length > MAX_BAGGAGE_BYTES) return undefined
  return /^[\x20-\x7e]+$/.test(trimmed) ? trimmed : undefined
}

/// `true` se [url] è tra le destinazioni fidate: una stringa matcha per substring
/// (stile `tracePropagationTargets` di Sentry), una RegExp è testata sull'URL
/// completo. I target stringa vuoti sono ignorati (non matcherebbero tutto).
export function matchesPropagationTarget(
  url: string,
  targets: readonly TracePropagationTarget[],
): boolean {
  for (const target of targets) {
    if (typeof target === 'string') {
      if (target.length > 0 && url.includes(target)) return true
    } else if (regExpMatches(target, url)) {
      return true
    }
  }
  return false
}

// `RegExp.test` con flag `g`/`y` avanza `lastIndex`, rendendo il match della
// stessa destinazione intermittente tra chiamate successive. Si testa su una
// copia priva di quei flag (gli altri — `i`, `m`, `s`, `u`, `d` — sono preservati),
// senza mutare la RegExp fornita dal consumer.
function regExpMatches(pattern: RegExp, url: string): boolean {
  if (!pattern.global && !pattern.sticky) return pattern.test(url)
  return new RegExp(pattern.source, pattern.flags.replace(/[gy]/g, '')).test(url)
}

/// `true` se [target] risolve nella stessa origine di [origin] (URL relativi
/// inclusi): la propria origine è first-party e fidata per definizione. `false`
/// su URL non parsabili.
export function isSameOrigin(target: string, origin: string): boolean {
  try {
    return new URL(target, origin).origin === origin
  } catch {
    return false
  }
}

/// Legge un header (case-insensitive) da un carrier eterogeneo: un getter
/// `(name) => value`, un `Headers`, o una mappa header (Node) con valori
/// stringa/array. Dei valori multipli prende il primo. `undefined` se assente.
export function readTraceHeader(
  carrier: TraceCarrier,
  name: string,
): string | undefined {
  if (typeof carrier === 'function') return orUndefined(carrier(name))
  if (typeof Headers !== 'undefined' && carrier instanceof Headers) {
    return orUndefined(carrier.get(name))
  }
  const wanted = name.toLowerCase()
  for (const key of Object.keys(carrier)) {
    if (key.toLowerCase() !== wanted) continue
    const value = (carrier as Record<string, string | string[] | undefined | null>)[key]
    if (Array.isArray(value))
      return value.length > 0 ? orUndefined(value[0]) : undefined
    return orUndefined(value)
  }
  return undefined
}

function orUndefined(value: string | null | undefined): string | undefined {
  return value === null || value === undefined ? undefined : value
}
